// TaskViewSidebar.js - Complete Updated Version with Full Features
import { useState, useEffect } from "react";
import { Button, Form, Badge, Row, Col, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getTaskById,
  updateTask,
  createSubTask,
  getSubTasksHierarchy,
  updateSubTask,
  deleteSubTask,
  addTaskRemark,
  getTaskRemarks,
  getAllTaskStatus,
} from "../Api/TaskMaster";
import "./TaskViewSidebar.css";
import { getUsersByIds } from "../Api/Auth";
import { getTeamById } from "../Api/TeamMaster";
import { fetchProjectDetails } from "../Api/ViewProjects";
import { fetchCusVenCpByCusVenType } from "../Api/CusVenCpMasterApi";
import { getPartnersByProject } from "../Api/PartnerMaster";
import { fetchStagesByProject } from "../Api/StageMaster";
import {
  getBuildingBlocksByOrgAndProject,
  getBuildingBlocksByTask,
  mapTaskToBuildingBlock,
  removeSpecificTaskMapping,
} from "../Api/BuildingBlockMaster";
import Select from "react-select";

const TaskViewSidebar = ({ show, onHide, taskId, onTaskUpdated }) => {
  // ==================== STATE DECLARATIONS ====================
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalTask, setOriginalTask] = useState(null);

  // Subtask State
  const [showSubTaskModal, setShowSubTaskModal] = useState(false);
  const [subTaskLoading, setSubTaskLoading] = useState(false);
  const [subTasks, setSubTasks] = useState([]);
  const [editingSubTask, setEditingSubTask] = useState(null);
  const [showEditSubTaskModal, setShowEditSubTaskModal] = useState(false);
  const [parentSubTaskId, setParentSubTaskId] = useState(null);

  // Remarks State
  const [remarks, setRemarks] = useState([]);
  const [remarkText, setRemarkText] = useState("");
  const [loadingRemarks, setLoadingRemarks] = useState(false);

  // Team and Contacts State
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMemberDetails, setTeamMemberDetails] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [clientContactPersons, setClientContactPersons] = useState([]);
  const [partnerContactPersons, setPartnerContactPersons] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Project Related State
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [buildingBlocks, setBuildingBlocks] = useState([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [existingBlockIds, setExistingBlockIds] = useState([]);

  // Status State
  const [statusList, setStatusList] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // User Info
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = String(userDetails?.userId);
  const orgId = userDetails?.organisationId;
  const username = userDetails?.username;
  const userRoles = userDetails?.roles || [];

  const [clientId, setClientId] = useState(null);
  const [projectPartners, setProjectPartners] = useState([]);

  // Role constants
  const PROJECT_ADMIN_ROLE = "Project_admin";
  const PROJECT_MANAGER_ROLE = "Project_Manager";
  const PROJECT_COMMERCIAL_ROLE = "Project_Commercial";

  // Role checking functions
  const hasProjectAdminRole = () => userRoles.includes(PROJECT_ADMIN_ROLE);
  const hasProjectManagerRole = () => userRoles.includes(PROJECT_MANAGER_ROLE);
  const hasProjectCommercialRole = () =>
    userRoles.includes(PROJECT_COMMERCIAL_ROLE);

  // Permission functions
  const canEditTask = () => hasProjectAdminRole() || hasProjectManagerRole();
  const canAddSubTask = () => hasProjectAdminRole() || hasProjectManagerRole();
  const canAddComment = () => true;

  // Helper function to get status name from GUID
  const getTaskStatusName = (statusGuid) => {
    if (!statusGuid) return "No Status";
    const status = statusList.find((s) => s.statusGuid === statusGuid);
    return status ? status.statusName : statusGuid;
  };

  // SubTask form state
  const [subTaskForm, setSubTaskForm] = useState({
    SubTaskName: "",
    SubTaskDescription: "",
    SubTaskAssignedTo: "",
    SubTaskStatus: "",
    SubTaskDueDate: "",
    SubTaskStartDate: "",
    SubTaskEndDate: "",
    SubTaskActualStateDate: "",
    SubTaskActualEndDate: "",
    SubTaskCreatedBy: "",
    ParentSubTaskId: null,
  });

  // ==================== API CALLS ====================

  const loadTaskStatus = async () => {
    try {
      setLoadingStatus(true);
      const data = await getAllTaskStatus();
      const sorted = data.sort((a, b) =>
        a.statusName.localeCompare(b.statusName),
      );
      setStatusList(sorted);
    } catch (error) {
      console.error("Error loading status:", error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const loadRemarks = async () => {
    try {
      setLoadingRemarks(true);
      const data = await getTaskRemarks(taskId);
      setRemarks(data || []);
    } catch (error) {
      console.error("Error loading remarks:", error);
    } finally {
      setLoadingRemarks(false);
    }
  };

  const fetchTeamMembers = async (projectId) => {
    if (!projectId) return;
    try {
      setLoadingTeam(true);
      const projectData = await fetchProjectDetails(projectId);
      if (projectData && projectData.teamAssigned) {
        const teamData = await getTeamById(projectData.teamAssigned);
        if (teamData && teamData.teamMembers) {
          setTeamMembers(teamData.teamMembers);
          await fetchUserDetails(teamData.teamMembers);
        } else {
          setTeamMembers([]);
          setTeamMemberDetails([]);
        }
      } else {
        setTeamMembers([]);
        setTeamMemberDetails([]);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      setTeamMembers([]);
      setTeamMemberDetails([]);
    } finally {
      setLoadingTeam(false);
    }
  };

  const fetchUserDetails = async (userIds) => {
    if (!userIds || userIds.length === 0) {
      setTeamMemberDetails([]);
      return;
    }
    try {
      const numericUserIds = userIds
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id));
      if (numericUserIds.length === 0) {
        setTeamMemberDetails([]);
        return;
      }
      const response = await getUsersByIds(numericUserIds);
      if (response && response.users) {
        setTeamMemberDetails(response.users);
      } else {
        setTeamMemberDetails([]);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setTeamMemberDetails([]);
    }
  };

  const fetchClientContactPersons = async (clientId) => {
    if (!clientId) return [];
    try {
      console.log("Fetching client contacts for:", clientId);
      const contacts = await fetchCusVenCpByCusVenType(clientId);
      if (Array.isArray(contacts)) {
        console.log("Client contacts found:", contacts);
        setClientContactPersons(contacts);
        return contacts;
      } else {
        setClientContactPersons([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching client contact persons:", error);
      setClientContactPersons([]);
      return [];
    }
  };

  const fetchPartnerContactPersons = async (partners) => {
    if (!partners || partners.length === 0) return [];
    try {
      setLoadingContacts(true);
      const allPartnerContacts = [];
      for (const partner of partners) {
        const partnerId = partner.partnerName;
        console.log("Fetching partner contacts for:", partnerId);
        const contacts = await fetchCusVenCpByCusVenType(partnerId);
        if (Array.isArray(contacts) && contacts.length > 0) {
          console.log(
            `Found ${contacts.length} contacts for partner ${partnerId}`,
          );
          allPartnerContacts.push(...contacts);
        }
      }
      console.log("Total partner contacts found:", allPartnerContacts.length);
      setPartnerContactPersons(allPartnerContacts);
      return allPartnerContacts;
    } catch (error) {
      console.error("Error fetching partner contact persons:", error);
      setPartnerContactPersons([]);
      return [];
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchProjectStages = async (projectId) => {
    if (!projectId) return;
    try {
      setLoadingStages(true);
      const stageData = await fetchStagesByProject(projectId);
      if (Array.isArray(stageData)) {
        setStages(stageData);
      } else {
        setStages([]);
      }
    } catch (error) {
      console.error("Error fetching stages:", error);
      setStages([]);
    } finally {
      setLoadingStages(false);
    }
  };

  const fetchBuildingBlocks = async (projectId) => {
    if (!projectId) return;
    try {
      setLoadingBlocks(true);
      const blocks = await getBuildingBlocksByOrgAndProject(orgId, projectId);
      setBuildingBlocks(blocks || []);
      const mappedBlocks = await getBuildingBlocksByTask(taskId);
      setExistingBlockIds(mappedBlocks || []);
      setSelectedBlockIds(mappedBlocks || []);
    } catch (error) {
      console.error("Error fetching building blocks:", error);
      setBuildingBlocks([]);
    } finally {
      setLoadingBlocks(false);
    }
  };

  // ==================== HELPER FUNCTIONS ====================

  const getAllAssigneeOptions = () => {
    const options = [];
    const seen = new Set();

    // Team members
    teamMembers.forEach((id) => {
      if (!seen.has(id)) {
        seen.add(id);
        const displayName = getUserDisplayName(id);
        options.push({
          value: id,
          label: `${displayName} (Team)`,
          type: "team",
        });
      }
    });

    // Client contacts
    clientContactPersons.forEach((contact) => {
      if (!seen.has(contact.cusVenId)) {
        seen.add(contact.cusVenId);
        options.push({
          value: contact.cusVenId,
          label: `${contact.contactPerName} ${contact.contactPerEmail ? `(${contact.contactPerEmail})` : ""} (Client Contact)`,
          type: "client",
        });
      }
    });

    // Partner contacts
    partnerContactPersons.forEach((contact) => {
      if (!seen.has(contact.cusVenId)) {
        seen.add(contact.cusVenId);
        options.push({
          value: contact.cusVenId,
          label: `${contact.contactPerName} ${contact.contactPerEmail ? `(${contact.contactPerEmail})` : ""} (Partner Contact)`,
          type: "partner",
        });
      }
    });

    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  };

  const getUserDisplayName = (memberId) => {
    if (!memberId) return "Unassigned";
    const userDetail = teamMemberDetails.find(
      (user) => user.userId === parseInt(memberId),
    );
    if (userDetail) {
      return `${userDetail.username} (${userDetail.email})`;
    }
    const clientContact = clientContactPersons.find(
      (c) => c.cusVenId === memberId,
    );
    if (clientContact) {
      return `${clientContact.contactPerName} (Client Contact)`;
    }
    const partnerContact = partnerContactPersons.find(
      (c) => c.cusVenId === memberId,
    );
    if (partnerContact) {
      return `${partnerContact.contactPerName} (Partner Contact)`;
    }
    return memberId;
  };

  const buildingBlockOptions = buildingBlocks.map((block) => ({
    value: block.buildingBlockId,
    label: `${block.buildingBlockName} (${block.buildingBlockId})`,
  }));

  const getStatusBadge = (status) => {
    const statusMap = {
      Active: "status-active",
      Completed: "status-completed",
      Pending: "status-pending",
      "In Progress": "status-in-progress",
      "Not Started": "status-pending",
      "On Hold": "status-pending",
    };
    return statusMap[status] || "status-pending";
  };

  const getPriorityBadge = (priority) => {
    const priorityMap = {
      High: "priority-high",
      Medium: "priority-medium",
      Low: "priority-low",
      Critical: "priority-high",
    };
    return priorityMap[priority] || "priority-medium";
  };

  const getStageDisplayName = (stageId) => {
    if (!stageId) return "No Stage";
    const stage = stages.find((s) => s.stageId === stageId);
    return stage ? `${stage.stageName} (${stage.stageId})` : stageId;
  };

  // ==================== TASK HANDLERS ====================

  const loadTaskDetails = async () => {
    setLoading(true);
    try {
      const taskData = await getTaskById(taskId);
      setTask(taskData);
      setOriginalTask(taskData);

      if (taskData.projectBelong) {
        await fetchTeamMembers(taskData.projectBelong);
        await fetchProjectStages(taskData.projectBelong);
        await fetchBuildingBlocks(taskData.projectBelong);

        const projectData = await fetchProjectDetails(taskData.projectBelong);
        if (projectData && projectData.prClient) {
          setClientId(projectData.prClient);
          await fetchClientContactPersons(projectData.prClient);
        }

        try {
          const partners = await getPartnersByProject(taskData.projectBelong);
          if (Array.isArray(partners) && partners.length > 0) {
            setProjectPartners(partners);
            await fetchPartnerContactPersons(partners);
          }
        } catch (error) {
          console.error("Error fetching partners:", error);
        }
      }
    } catch (error) {
      console.error("Error loading task details:", error);
      toast.error("Failed to load task details");
    } finally {
      setLoading(false);
    }
  };

  const loadSubTasks = async () => {
    try {
      const subtasksData = await getSubTasksHierarchy(taskId);
      setSubTasks(subtasksData || []);
    } catch (error) {
      console.error("Error loading subtasks hierarchy:", error);
      setSubTasks([]);
    }
  };

  const handleFieldChange = (field, value) => {
    if (!isEditing) setIsEditing(true);
    setTask((prev) => ({ ...prev, [field]: value }));
  };

  const handleBuildingBlockChange = (selectedOptions) => {
    if (!isEditing) setIsEditing(true);
    const values = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    setSelectedBlockIds(values);
  };

  const handleSave = async () => {
    if (!task) return;
    setSaving(true);
    try {
      const updateData = {
        TaskName: task.taskName,
        TaskDescription: task.taskDescription,
        AssignedTo: task.assignedTo,
        TaskPriority: task.taskPriority,
        TaskStatus: task.taskStatus,
        TaskStartDate: task.taskStartDate,
        TaskEndDate: task.taskEndDate,
        TaskType: task.taskType,
        ActualStartDate: task.actualStartDate,
        ActualEndDate: task.actualEndDate,
        StageBelong: task.stageBelong,
        TaskTags: task.taskTags,
        Comments: task.comments,
        EditedBy: username,
      };

      await updateTask(taskId, updateData);

      // Handle Building Block Mapping
      try {
        const blocksToAdd = selectedBlockIds.filter(
          (id) => !existingBlockIds.includes(id),
        );
        const blocksToRemove = existingBlockIds.filter(
          (id) => !selectedBlockIds.includes(id),
        );

        for (const blockId of blocksToAdd) {
          await mapTaskToBuildingBlock({
            buildingBlockId: blockId,
            taskId: taskId,
            orgId: String(orgId),
            createdBy: String(userId),
          });
        }

        for (const blockId of blocksToRemove) {
          await removeSpecificTaskMapping(taskId, blockId);
        }

        setExistingBlockIds([...selectedBlockIds]);
      } catch (err) {
        console.error("Failed updating building block mappings", err);
      }

      setOriginalTask(task);
      setIsEditing(false);
      toast.success("Task updated successfully");
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setTask(originalTask);
    setSelectedBlockIds([...existingBlockIds]);
    setIsEditing(false);
  };

  const handleClose = () => {
    if (isEditing) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to close?",
        )
      ) {
        onHide();
      }
    } else {
      onHide();
    }
  };

  // ==================== REMARK HANDLERS ====================

  const handleAddRemark = async () => {
    if (!remarkText.trim()) {
      toast.error("Remark cannot be empty");
      return;
    }
    try {
      await addTaskRemark({
        taskId: taskId,
        remark: remarkText,
        createdBy: userId,
      });
      toast.success("Remark added");
      setRemarkText("");
      loadRemarks();
    } catch (error) {
      console.error(error);
      toast.error("Failed to add remark");
    }
  };

  // ==================== SUBTASK HANDLERS ====================

  const handleAddSubTask = (parentId = null) => {
    setParentSubTaskId(parentId);
    setSubTaskForm({
      SubTaskName: "",
      SubTaskDescription: "",
      SubTaskAssignedTo: task?.assignedTo || "",
      SubTaskStatus: "",
      SubTaskDueDate: task?.taskEndDate
        ? moment(task.taskEndDate).format("YYYY-MM-DD")
        : "",
      SubTaskStartDate: "",
      SubTaskEndDate: "",
      SubTaskActualStateDate: "",
      SubTaskActualEndDate: "",
      SubTaskCreatedBy: userId,
      ParentSubTaskId: parentId,
    });
    setShowSubTaskModal(true);
  };

  const handleSubTaskFormChange = (field, value) => {
    setSubTaskForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubTaskSubmit = async () => {
    if (!subTaskForm.SubTaskName.trim()) {
      toast.error("SubTask name is required");
      return;
    }
    setSubTaskLoading(true);
    try {
      const subTaskData = {
        ...subTaskForm,
        SubTaskBelongTask: taskId,
        SubTaskCreatedBy: userId,

        // 🔥 FIX: send null instead of ""
        SubTaskStartDate: subTaskForm.SubTaskStartDate || null,
        SubTaskEndDate: subTaskForm.SubTaskEndDate || null,
        SubTaskDueDate: subTaskForm.SubTaskDueDate || null,
        SubTaskActualStateDate: subTaskForm.SubTaskActualStateDate || null,
        SubTaskActualEndDate: subTaskForm.SubTaskActualEndDate || null,
      };
      await createSubTask(subTaskData, username);
      toast.success("SubTask created successfully");
      setShowSubTaskModal(false);
      await loadSubTasks();
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      console.error("Error creating subtask:", error);
      toast.error("Failed to create subtask");
    } finally {
      setSubTaskLoading(false);
    }
  };

  const handleEditSubTask = (subTask) => {
    setEditingSubTask(subTask);
    setSubTaskForm({
      SubTaskName: subTask.subTaskName || "",
      SubTaskDescription: subTask.subTaskDescription || "",
      SubTaskAssignedTo: subTask.subTaskAssignedTo || "",
      SubTaskStatus: subTask.subTaskStatus || "",
      SubTaskDueDate: subTask.subTaskDueDate
        ? moment(subTask.subTaskDueDate).format("YYYY-MM-DD")
        : "",
      SubTaskStartDate: subTask.subTaskStartDate
        ? moment(subTask.subTaskStartDate).format("YYYY-MM-DD")
        : "",
      SubTaskEndDate: subTask.subTaskEndDate
        ? moment(subTask.subTaskEndDate).format("YYYY-MM-DD")
        : "",
      SubTaskActualStateDate: subTask.subTaskActualStateDate
        ? moment(subTask.subTaskActualStateDate).format("YYYY-MM-DD")
        : "",
      SubTaskActualEndDate: subTask.subTaskActualEndDate
        ? moment(subTask.subTaskActualEndDate).format("YYYY-MM-DD")
        : "",
      SubTaskCreatedBy: userId,
      ParentSubTaskId: subTask.parentSubTaskId || null,
    });
    setShowEditSubTaskModal(true);
  };

  const handleUpdateSubTask = async () => {
    if (!editingSubTask || !subTaskForm.SubTaskName.trim()) {
      toast.error("SubTask name is required");
      return;
    }
    setSubTaskLoading(true);
    try {
      const updateData = {
        SubTaskName: subTaskForm.SubTaskName,
        SubTaskDescription: subTaskForm.SubTaskDescription,
        SubTaskAssignedTo: subTaskForm.SubTaskAssignedTo,
        SubTaskStatus: subTaskForm.SubTaskStatus,
        SubTaskDueDate: subTaskForm.SubTaskDueDate || null,
        SubTaskStartDate: subTaskForm.SubTaskStartDate || null,
        SubTaskEndDate: subTaskForm.SubTaskEndDate || null,
        SubTaskActualStateDate: subTaskForm.SubTaskActualStateDate || null,
        SubTaskActualEndDate: subTaskForm.SubTaskActualEndDate || null,
        SubTaskEditedBy: userId,
      };
      await updateSubTask(editingSubTask.subTaskId, updateData);
      toast.success("SubTask updated successfully");
      setShowEditSubTaskModal(false);
      setEditingSubTask(null);
      await loadSubTasks();
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      console.error("Error updating subtask:", error);
      toast.error("Failed to update subtask");
    } finally {
      setSubTaskLoading(false);
    }
  };

  const handleDeleteSubTask = async (subTaskId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this subtask and all its children?",
      )
    ) {
      return;
    }
    try {
      await deleteSubTask(subTaskId);
      toast.success("SubTask deleted successfully");
      await loadSubTasks();
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      console.error("Error deleting subtask:", error);
      toast.error("Failed to delete subtask");
    }
  };

  // ==================== RECURSIVE SUBTASK RENDERER ====================

  const renderSubTasks = (tasks, level = 0) => {
    if (!tasks || tasks.length === 0) return null;

    return tasks.map((subTask) => (
      <div
        key={subTask.subTaskId}
        style={{
          marginLeft: `${level * 20}px`,
          borderLeft: level > 0 ? "2px solid #e0e0e0" : "none",
          paddingLeft: level > 0 ? "12px" : "0",
          marginBottom: "12px",
        }}
      >
        <div
          className="subtask-item"
          style={{
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            padding: "12px",
            border: "1px solid #e9ecef",
            transition: "all 0.2s ease",
          }}
        >
          <div
            className="subtask-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: "14px", color: "#2c3e50" }}>
                {subTask.subTaskName}
              </strong>
              {subTask.subTaskDescription && (
                <div
                  className="subtask-description"
                  style={{
                    fontSize: "12px",
                    color: "#6c757d",
                    marginTop: "4px",
                    lineHeight: "1.4",
                  }}
                >
                  {subTask.subTaskDescription}
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <span
                className={`subtask-status ${getStatusBadge(getTaskStatusName(subTask.subTaskStatus))}`}
                style={{
                  fontSize: "11px",
                  padding: "4px 10px",
                  borderRadius: "12px",
                }}
              >
                {getTaskStatusName(subTask.subTaskStatus)}
              </span>
              {canAddSubTask() && (
                <>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleAddSubTask(subTask.subTaskId)}
                    title="Add child subtask"
                    style={{ padding: "4px 10px", fontSize: "12px" }}
                  >
                    <i className="fas fa-plus me-1"></i> Child
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleEditSubTask(subTask)}
                    title="Edit subtask"
                    style={{ padding: "4px 10px", fontSize: "12px" }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteSubTask(subTask.subTaskId)}
                    title="Delete subtask"
                    style={{ padding: "4px 10px", fontSize: "12px" }}
                  >
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>
          <div
            className="subtask-meta"
            style={{
              fontSize: "11px",
              color: "#6c757d",
              marginTop: "8px",
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            {subTask.subTaskAssignedTo && (
              <span>
                <i className="fas fa-user me-1"></i>
                {getUserDisplayName(subTask.subTaskAssignedTo)}
              </span>
            )}
            {subTask.subTaskStartDate && (
              <span>
                <i className="fas fa-calendar-alt me-1"></i>
                Start: {moment(subTask.subTaskStartDate).format("MMM DD, YYYY")}
              </span>
            )}
            {subTask.subTaskEndDate && (
              <span>
                <i className="fas fa-flag-checkered me-1"></i>
                Due: {moment(subTask.subTaskEndDate).format("MMM DD, YYYY")}
              </span>
            )}
            {subTask.subTaskActualStateDate && (
              <span>
                <i className="fas fa-play-circle me-1"></i>
                Actual Start:{" "}
                {moment(subTask.subTaskActualStateDate).format("MMM DD, YYYY")}
              </span>
            )}
            {subTask.subTaskActualEndDate && (
              <span>
                <i className="fas fa-check-circle me-1"></i>
                Actual End:{" "}
                {moment(subTask.subTaskActualEndDate).format("MMM DD, YYYY")}
              </span>
            )}
          </div>
        </div>

        {/* Recursive children rendering */}
        {subTask.children &&
          subTask.children.length > 0 &&
          renderSubTasks(subTask.children, level + 1)}
      </div>
    ));
  };

  // ==================== EFFECTS ====================

  useEffect(() => {
    if (show && taskId) {
      loadTaskDetails();
      loadSubTasks();
      loadRemarks();
      loadTaskStatus();
    } else {
      setTask(null);
      setOriginalTask(null);
      setIsEditing(false);
      setSubTasks([]);
      setRemarks([]);
      setTeamMembers([]);
      setTeamMemberDetails([]);
    }
  }, [show, taskId]);

  // ==================== RENDER ====================

  if (!show) return null;

  return (
    <>
      <div className="task-sidebar-overlay" onClick={handleClose}></div>
      <div className="task-view-sidebar">
        <div className="task-sidebar-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 task-sidebar-title">
              <i className="fas fa-tasks me-2"></i>
              Task Details
            </h5>
            <Button
              variant="link"
              className="task-close-btn"
              onClick={handleClose}
              title="Close sidebar"
            >
              <i className="fas fa-times"></i>
            </Button>
          </div>

          {isEditing && canEditTask() && (
            <div className="edit-controls" style={{ marginTop: "10px" }}>
              <Button
                variant="success"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="me-2"
              >
                <i className="fas fa-save me-1"></i>
                {saving ? "Saving..." : "Save Changes"}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleCancel}
                disabled={saving}
              >
                <i className="fas fa-times me-1"></i>
                Cancel
              </Button>
            </div>
          )}
        </div>

        <div className="task-sidebar-content">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading task details...</span>
              </div>
              <p className="mt-2 text-muted">Loading task details...</p>
            </div>
          ) : task ? (
            <div className="task-details">
              {/* Task Name */}
              <div className="task-field-group">
                <label className="task-field-label">Task Name</label>
                {isEditing && canEditTask() ? (
                  <Form.Control
                    type="text"
                    value={task.taskName || ""}
                    onChange={(e) =>
                      handleFieldChange("taskName", e.target.value)
                    }
                    className="task-field-input"
                    placeholder="Enter task name"
                  />
                ) : (
                  <div
                    className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                    onClick={() => canEditTask() && setIsEditing(true)}
                    title={canEditTask() ? "Click to edit" : ""}
                  >
                    <p style={{ fontSize: "15px", margin: "0", padding: "0" }}>
                      {task.taskName}
                    </p>
                    {!canEditTask() && (
                      <small className="text-muted d-block">(Read only)</small>
                    )}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="task-field-group">
                <label className="task-field-label">Description</label>
                {isEditing && canEditTask() ? (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={task.taskDescription || ""}
                    onChange={(e) =>
                      handleFieldChange("taskDescription", e.target.value)
                    }
                    className="task-field-input"
                    placeholder="Enter task description"
                  />
                ) : (
                  <div
                    className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                    onClick={() => canEditTask() && setIsEditing(true)}
                    title={canEditTask() ? "Click to edit" : ""}
                  >
                    {task.taskDescription || (
                      <span className="text-muted">
                        No description provided
                      </span>
                    )}
                  </div>
                )}
              </div>

              <Row className="g-3">
                {/* Task Type */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">Task Type</label>
                    {isEditing && canEditTask() ? (
                      <Form.Select
                        value={task.taskType || ""}
                        onChange={(e) =>
                          handleFieldChange("taskType", e.target.value)
                        }
                        className="task-field-input"
                      >
                        <option value="">Select Task Type</option>
                        <option value="Development">Development</option>
                        <option value="Design">Design</option>
                        <option value="Testing">Testing</option>
                        <option value="Documentation">Documentation</option>
                        <option value="Research">Research</option>
                        <option value="Meeting">Meeting</option>
                        <option value="Maintenance">Maintenance</option>
                      </Form.Select>
                    ) : (
                      <div
                        className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                        onClick={() => canEditTask() && setIsEditing(true)}
                        style={{ fontSize: "13px" }}
                      >
                        {task.taskType || (
                          <span className="text-muted">Not specified</span>
                        )}
                      </div>
                    )}
                  </div>
                </Col>

                {/* Status */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">Status</label>
                    {isEditing && canEditTask() ? (
                      <Form.Select
                        value={task.taskStatus || ""}
                        onChange={(e) =>
                          handleFieldChange("taskStatus", e.target.value)
                        }
                        className="task-field-input"
                      >
                        <option value="">
                          {loadingStatus ? "Loading..." : "Select Status"}
                        </option>
                        {statusList.map((status) => (
                          <option
                            key={status.statusGuid}
                            value={status.statusGuid}
                          >
                            {status.statusName}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <div className="task-field-value">
                        <span
                          className={`task-badge ${getStatusBadge(getTaskStatusName(task.taskStatus))}`}
                        >
                          {getTaskStatusName(task.taskStatus)}
                        </span>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="g-3">
                {/* Priority */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">Priority</label>
                    {isEditing && canEditTask() ? (
                      <Form.Select
                        value={task.taskPriority || ""}
                        onChange={(e) =>
                          handleFieldChange("taskPriority", e.target.value)
                        }
                        className="task-field-input"
                      >
                        <option value="">Select Priority</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Critical">Critical</option>
                      </Form.Select>
                    ) : (
                      <div className="task-field-value">
                        <span
                          className={`task-badge ${getPriorityBadge(task.taskPriority)}`}
                        >
                          {task.taskPriority}
                        </span>
                      </div>
                    )}
                  </div>
                </Col>

                {/* Stage */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">Stage</label>
                    {isEditing && canEditTask() ? (
                      loadingStages ? (
                        <Form.Control
                          type="text"
                          disabled
                          value="Loading stages..."
                          className="bg-light"
                        />
                      ) : (
                        <Form.Select
                          value={task.stageBelong || ""}
                          onChange={(e) =>
                            handleFieldChange("stageBelong", e.target.value)
                          }
                          className="task-field-input"
                        >
                          <option value="">Select Stage (Optional)</option>
                          {stages.map((stage) => (
                            <option key={stage.stageId} value={stage.stageId}>
                              {stage.stageName} ({stage.stageId})
                            </option>
                          ))}
                        </Form.Select>
                      )
                    ) : (
                      <div
                        className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                        onClick={() => canEditTask() && setIsEditing(true)}
                        style={{ fontSize: "13px" }}
                      >
                        {getStageDisplayName(task.stageBelong)}
                      </div>
                    )}
                    {stages.length === 0 && !loadingStages && !isEditing && (
                      <Form.Text
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        No stages available for this project
                      </Form.Text>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="g-3">
                {/* Assigned To */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">
                      <i className="fas fa-user me-2"></i>
                      Assigned To
                    </label>
                    {isEditing && canEditTask() ? (
                      loadingTeam || loadingContacts ? (
                        <Form.Control
                          type="text"
                          disabled
                          value="Loading assignees..."
                          className="bg-light"
                        />
                      ) : (
                        <Form.Select
                          value={task.assignedTo || ""}
                          onChange={(e) =>
                            handleFieldChange("assignedTo", e.target.value)
                          }
                          className="task-field-input"
                        >
                          <option value="">Select Assignee</option>
                          {getAllAssigneeOptions().map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      )
                    ) : (
                      <div
                        className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                        onClick={() => canEditTask() && setIsEditing(true)}
                        title={canEditTask() ? "Click to edit" : ""}
                        style={{ fontSize: "13px" }}
                      >
                        {getUserDisplayName(task.assignedTo) || (
                          <span className="text-muted">Unassigned</span>
                        )}
                      </div>
                    )}
                  </div>
                </Col>

                {/* Task ID */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">
                      <i className="fas fa-hashtag me-2"></i>
                      Task ID
                    </label>
                    <div
                      className="task-field-value task-id-value"
                      style={{ fontSize: "13px" }}
                    >
                      {task.taskId}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="g-3">
                {/* Planned Start Date */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">
                      <i className="fas fa-play-circle me-2"></i>
                      Planned Start Date
                    </label>
                    {isEditing && canEditTask() ? (
                      <Form.Control
                        type="date"
                        value={
                          task.taskStartDate
                            ? moment(task.taskStartDate).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) =>
                          handleFieldChange("taskStartDate", e.target.value)
                        }
                        className="task-field-input"
                      />
                    ) : (
                      <div
                        className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                        onClick={() => canEditTask() && setIsEditing(true)}
                        style={{ fontSize: "13px" }}
                      >
                        {task.taskStartDate ? (
                          <>
                            <i className="fas fa-calendar me-1"></i>
                            {moment(task.taskStartDate).format("MMM DD, YYYY")}
                          </>
                        ) : (
                          <span className="text-muted">Not set</span>
                        )}
                      </div>
                    )}
                  </div>
                </Col>

                {/* Planned End Date */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">
                      <i className="fas fa-flag-checkered me-2"></i>
                      Planned End Date
                    </label>
                    {isEditing && canEditTask() ? (
                      <Form.Control
                        type="date"
                        value={
                          task.taskEndDate
                            ? moment(task.taskEndDate).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) =>
                          handleFieldChange("taskEndDate", e.target.value)
                        }
                        className="task-field-input"
                      />
                    ) : (
                      <div
                        className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                        onClick={() => canEditTask() && setIsEditing(true)}
                        style={{ fontSize: "13px" }}
                      >
                        {task.taskEndDate ? (
                          <>
                            <i className="fas fa-calendar me-1"></i>
                            {moment(task.taskEndDate).format("MMM DD, YYYY")}
                          </>
                        ) : (
                          <span className="text-muted">Not set</span>
                        )}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="g-3">
                {/* Actual Start Date */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">
                      <i className="fas fa-play-circle me-2"></i>
                      Actual Start Date
                    </label>
                    {isEditing && canEditTask() ? (
                      <Form.Control
                        type="date"
                        value={
                          task.actualStartDate
                            ? moment(task.actualStartDate).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) =>
                          handleFieldChange("actualStartDate", e.target.value)
                        }
                        className="task-field-input"
                      />
                    ) : (
                      <div
                        className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                        onClick={() => canEditTask() && setIsEditing(true)}
                        style={{ fontSize: "13px" }}
                      >
                        {task.actualStartDate ? (
                          <>
                            <i className="fas fa-calendar me-1"></i>
                            {moment(task.actualStartDate).format(
                              "MMM DD, YYYY",
                            )}
                          </>
                        ) : (
                          <span className="text-muted">Not started</span>
                        )}
                      </div>
                    )}
                  </div>
                </Col>

                {/* Actual End Date */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">
                      <i className="fas fa-flag-checkered me-2"></i>
                      Actual End Date
                    </label>
                    {isEditing && canEditTask() ? (
                      <Form.Control
                        type="date"
                        value={
                          task.actualEndDate
                            ? moment(task.actualEndDate).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) =>
                          handleFieldChange("actualEndDate", e.target.value)
                        }
                        className="task-field-input"
                      />
                    ) : (
                      <div
                        className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                        onClick={() => canEditTask() && setIsEditing(true)}
                        style={{ fontSize: "13px" }}
                      >
                        {task.actualEndDate ? (
                          <>
                            <i className="fas fa-calendar me-1"></i>
                            {moment(task.actualEndDate).format("MMM DD, YYYY")}
                          </>
                        ) : (
                          <span className="text-muted">Not completed</span>
                        )}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Building Blocks */}
              <div className="task-field-group">
                <label className="task-field-label">
                  <i className="fas fa-cubes me-2"></i>
                  Building Blocks
                </label>
                {isEditing && canEditTask() ? (
                  loadingBlocks ? (
                    <Form.Control
                      type="text"
                      disabled
                      value="Loading building blocks..."
                      className="bg-light"
                    />
                  ) : (
                    <Select
                      isMulti
                      options={buildingBlockOptions}
                      value={buildingBlockOptions.filter((opt) =>
                        selectedBlockIds.includes(opt.value),
                      )}
                      onChange={handleBuildingBlockChange}
                      placeholder="Select Building Blocks..."
                    />
                  )
                ) : (
                  <div
                    className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                    onClick={() => canEditTask() && setIsEditing(true)}
                  >
                    {selectedBlockIds.length > 0 ? (
                      <div className="task-tags">
                        {selectedBlockIds.map((blockId) => {
                          const block = buildingBlocks.find(
                            (b) => b.buildingBlockId === blockId,
                          );
                          return (
                            <Badge
                              key={blockId}
                              bg="info"
                              text="dark"
                              className="me-1 mb-1"
                              style={{ fontSize: "12px" }}
                            >
                              {block ? block.buildingBlockName : blockId}
                            </Badge>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-muted">
                        No building blocks assigned
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Tags */}
              <div className="task-field-group">
                <label className="task-field-label">
                  <i className="fas fa-tags me-2"></i>
                  Tags
                </label>
                {isEditing && canEditTask() ? (
                  <Form.Control
                    type="text"
                    value={task.taskTags || ""}
                    onChange={(e) =>
                      handleFieldChange("taskTags", e.target.value)
                    }
                    className="task-field-input"
                    placeholder="Comma separated tags (e.g., design, development, urgent)"
                  />
                ) : (
                  <div
                    className={`task-field-value ${canEditTask() ? "editable-field" : ""}`}
                    onClick={() => canEditTask() && setIsEditing(true)}
                  >
                    {task.taskTags ? (
                      <div className="task-tags">
                        {task.taskTags.split(",").map((tag, index) => (
                          <Badge
                            key={index}
                            bg="light"
                            text="dark"
                            className="me-1 mb-1"
                            style={{ fontSize: "13px" }}
                          >
                            {tag.trim()}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        No tags
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Remarks Section */}
              <div className="task-field-group">
                <label className="task-field-label">
                  <i className="fas fa-comment-dots me-2"></i>
                  Remarks ({remarks.length})
                </label>

                {canAddComment() && (
                  <div className="d-flex mb-2">
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Write a remark..."
                      value={remarkText}
                      onChange={(e) => setRemarkText(e.target.value)}
                      className="me-2"
                    />
                    <Button
                      variant="primary"
                      onClick={handleAddRemark}
                      style={{ height: "38px" }}
                      disabled={!remarkText.trim()}
                    >
                      Add
                    </Button>
                  </div>
                )}

                <div className="remarks-list">
                  {loadingRemarks ? (
                    <div className="text-muted">Loading remarks...</div>
                  ) : remarks.length > 0 ? (
                    remarks.map((r) => (
                      <div key={r.taskLogId} className="remark-item">
                        <div className="remark-header">
                          <strong>{r.createdBy}</strong>
                          <span className="text-muted ms-2">
                            {moment(r.createdDate).format("MMM DD")}{" "}
                            {moment(r.createdTime, "HH:mm:ss").format(
                              "hh:mm A",
                            )}
                          </span>
                        </div>
                        <div className="remark-text">{r.logDescription}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-muted">No remarks yet</div>
                  )}
                </div>
              </div>

              {/* SubTasks Section - Tree View */}
              <div className="task-field-group">
                <label
                  className="task-field-label"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    <i className="fas fa-tasks me-2"></i>
                    Sub Tasks ({subTasks.length})
                  </span>
                  {canAddSubTask() && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleAddSubTask(null)}
                    >
                      <i className="fas fa-plus me-1"></i>
                      Add Sub Task
                    </Button>
                  )}
                </label>
                <div
                  className="subtasks-list"
                  style={{
                    marginTop: "12px",
                    maxHeight: "500px",
                    overflowY: "auto",
                  }}
                >
                  {subTasks.length > 0 ? (
                    renderSubTasks(subTasks)
                  ) : (
                    <div
                      className="text-muted text-center py-4"
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                      }}
                    >
                      <i
                        className="fas fa-tasks fa-2x mb-2"
                        style={{ opacity: 0.5 }}
                      ></i>
                      <p className="mb-0">No sub tasks yet.</p>
                      {canAddSubTask() && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mt-2"
                          onClick={() => handleAddSubTask(null)}
                        >
                          Create First Sub Task
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Created Info */}
              <div className="task-field-group">
                <div className="task-meta-info">
                  <small className="text-muted">
                    <i className="fas fa-clock me-1"></i>
                    Created{" "}
                    {task.createdDate
                      ? moment(task.createdDate).format(
                          "MMM DD, YYYY [at] h:mm A",
                        )
                      : "N/A"}
                    {task.createdBy && ` by ${task.createdBy}`}
                  </small>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              <i className="fas fa-exclamation-triangle fa-2x mb-3"></i>
              <p>Task not found or unable to load task details.</p>
              <Button variant="outline-secondary" onClick={handleClose}>
                Close
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Add SubTask Modal */}
      {canAddSubTask() && (
        <Modal
          show={showSubTaskModal}
          onHide={() => setShowSubTaskModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-plus-circle me-2"></i>
              {parentSubTaskId ? "Add Child Sub Task" : "Add New Sub Task"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>
                      SubTask Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={subTaskForm.SubTaskName}
                      onChange={(e) =>
                        handleSubTaskFormChange("SubTaskName", e.target.value)
                      }
                      placeholder="Enter subtask name"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={subTaskForm.SubTaskDescription}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskDescription",
                          e.target.value,
                        )
                      }
                      placeholder="Enter subtask description"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Assigned To</Form.Label>
                    {loadingTeam || loadingContacts ? (
                      <Form.Control
                        type="text"
                        disabled
                        value="Loading assignees..."
                        className="bg-light"
                      />
                    ) : (
                      <Form.Select
                        value={subTaskForm.SubTaskAssignedTo || ""}
                        onChange={(e) =>
                          handleSubTaskFormChange(
                            "SubTaskAssignedTo",
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Select Assignee</option>
                        {getAllAssigneeOptions().map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={subTaskForm.SubTaskStatus}
                      onChange={(e) =>
                        handleSubTaskFormChange("SubTaskStatus", e.target.value)
                      }
                    >
                      <option value="">Select Status</option>
                      {statusList.map((status) => (
                        <option
                          key={status.statusGuid}
                          value={status.statusGuid}
                        >
                          {status.statusName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={subTaskForm.SubTaskStartDate}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskStartDate",
                          e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={subTaskForm.SubTaskEndDate}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskEndDate",
                          e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Actual Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={subTaskForm.SubTaskActualStateDate}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskActualStateDate",
                          e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Actual End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={subTaskForm.SubTaskActualEndDate}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskActualEndDate",
                          e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowSubTaskModal(false)}
              disabled={subTaskLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubTaskSubmit}
              disabled={subTaskLoading || !subTaskForm.SubTaskName.trim()}
            >
              {subTaskLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-1"></i>
                  Create Sub Task
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Edit SubTask Modal */}
      {canAddSubTask() && (
        <Modal
          show={showEditSubTaskModal}
          onHide={() => {
            setShowEditSubTaskModal(false);
            setEditingSubTask(null);
          }}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-edit me-2"></i>
              Edit Sub Task
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>
                      SubTask Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={subTaskForm.SubTaskName}
                      onChange={(e) =>
                        handleSubTaskFormChange("SubTaskName", e.target.value)
                      }
                      placeholder="Enter subtask name"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={subTaskForm.SubTaskDescription}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskDescription",
                          e.target.value,
                        )
                      }
                      placeholder="Enter subtask description"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Assigned To</Form.Label>
                    {loadingTeam || loadingContacts ? (
                      <Form.Control
                        type="text"
                        disabled
                        value="Loading assignees..."
                        className="bg-light"
                      />
                    ) : (
                      <Form.Select
                        value={subTaskForm.SubTaskAssignedTo || ""}
                        onChange={(e) =>
                          handleSubTaskFormChange(
                            "SubTaskAssignedTo",
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Select Assignee</option>
                        {getAllAssigneeOptions().map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={subTaskForm.SubTaskStatus}
                      onChange={(e) =>
                        handleSubTaskFormChange("SubTaskStatus", e.target.value)
                      }
                    >
                      <option value="">Select Status</option>
                      {statusList.map((status) => (
                        <option
                          key={status.statusGuid}
                          value={status.statusGuid}
                        >
                          {status.statusName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={subTaskForm.SubTaskStartDate}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskStartDate",
                          e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={subTaskForm.SubTaskEndDate}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskEndDate",
                          e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Actual Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={subTaskForm.SubTaskActualStateDate}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskActualStateDate",
                          e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Actual End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={subTaskForm.SubTaskActualEndDate}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskActualEndDate",
                          e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowEditSubTaskModal(false);
                setEditingSubTask(null);
              }}
              disabled={subTaskLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateSubTask}
              disabled={subTaskLoading || !subTaskForm.SubTaskName.trim()}
            >
              {subTaskLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Updating...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-1"></i>
                  Update Sub Task
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default TaskViewSidebar;
